/**
 * Base handler with common functionality
 */
import DOMUtils from '../utils/DOMUtils';
import { FIELD_SELECTORS } from '../constants';

class BaseHandler {
  constructor(form, api, stepNavigator, messages) {
    this.form = form;
    this.api = api;
    this.stepNavigator = stepNavigator;
    this.messages = messages;
  }

  /**
   * Get error message from API response or fallback
   */
  getErrorMessage(data, fallbackKey = 'error_generic') {
    return this.messages.getMessageFromApi(data.message, {})
      || this.messages.getMessage(fallbackKey);
  }

  /**
   * Get redirect URL stored on form (if any)
   */
  getRedirectUrl() {
    return this.form?.dataset?.redirectUrl || null;
  }

  /**
   * Initialize reset password email field from URL parameter
   */
  initResetPasswordEmail() {
    const urlParams = new URLSearchParams(window.location.search);
    const login = urlParams.get('login');
    const resetEmailInput = this.form.querySelector(FIELD_SELECTORS.resetEmail);

    if (login && resetEmailInput) {
      resetEmailInput.value = decodeURIComponent(login);
    }
  }

  /**
   * Handle API call with common patterns (loading, error handling, success)
   */
  async handleApiCall(formSelector, apiCall, options = {}) {
    const {
      successCallback,
      errorCallback,
      loadingForm = null,
      messageElement = null,
      successMessageKey = null,
      errorMessageKey = 'error_generic',
      hideMessageOnStart = true,
      showPreloader = false,
    } = options;

    const form = formSelector ? this.form.querySelector(formSelector) : null;
    const loadingFormEl = loadingForm || form;

    if (loadingFormEl) {
      DOMUtils.setLoading(loadingFormEl, true, this.form);
    }
    if (messageElement && hideMessageOnStart) {
      DOMUtils.hideMessage(messageElement);
    }

    if (showPreloader) {
      DOMUtils.showPreloader(this.form);
    }

    try {
      const { response, data } = await apiCall();

      if (response.ok) {
        if (successMessageKey && messageElement) {
          const message = this.getErrorMessage(data, successMessageKey);
          DOMUtils.showMessage(messageElement, message, 'success');
        }
        if (successCallback) {
          successCallback(data);
        }
      } else {
        if (showPreloader) {
          DOMUtils.hidePreloader(this.form);
        }
        const message = this.getErrorMessage(data, errorMessageKey);
        if (messageElement) {
          DOMUtils.showMessage(messageElement, message, 'error');
        } else if (errorCallback) {
          errorCallback(data, message);
        }
      }
    } catch (error) {
      if (showPreloader) {
        DOMUtils.hidePreloader(this.form);
      }
      const message = this.getErrorMessage(error, 'error_connection');
      if (messageElement) {
        DOMUtils.showMessage(messageElement, message, 'error');
      } else if (errorCallback) {
        errorCallback(error, message);
      }
    } finally {
      if (loadingFormEl) {
        DOMUtils.setLoading(loadingFormEl, false, this.form);
      }
    }
  }
}

export default BaseHandler;
